import type React from 'react'
import { useEffect, useState } from 'react'
import { Search, Plus, X, Cake } from 'lucide-react'
import { recipesApi, type Recipe, type Dessert } from '../../services/api'
import { useAuth } from '../../contexts/useAuth'

interface Props {
    state: {
        desserts: Dessert[]
        nbPersons: number
    }
    update: (patch: Partial<Props['state']>) => void
    onNext: () => void
}

export default function Step2Desserts({ state, update, onNext }: Props) {
    const { isAuthenticated, loading: authLoading } = useAuth()
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<Recipe[]>([])

    useEffect(() => {
        if (authLoading) return
        if (!query.trim()) { setTimeout(() => setResults([]), 0); return }
        const t = setTimeout(() => {
            recipesApi.list(query, 'dessert').then(setResults).catch(() => { })
        }, 300)
        return () => clearTimeout(t)
    }, [query, isAuthenticated, authLoading])

    const addDessert = (r: Recipe) => {
        if (state.desserts.find(d => d.recipe_id === r.id)) return
        update({ desserts: [...state.desserts, { recipe_id: r.id, nb_repetitions: 1, nb_persons: state.nbPersons, _name: r.name } as Dessert & { _name?: string }] })
        setQuery('')
        setResults([])
    }

    const updateDessert = (idx: number, patch: Partial<Dessert>) => {
        const updated = [...state.desserts]
        updated[idx] = { ...updated[idx], ...patch }
        update({ desserts: updated })
    }

    const removeDessert = (idx: number) => {
        update({ desserts: state.desserts.filter((_, i) => i !== idx) })
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ padding: '12px 16px', background: 'var(--primary-glow)', borderRadius: 'var(--radius)', border: '1px solid var(--primary)' }}>
                <p style={{ fontSize: '0.85rem', color: 'var(--primary)', fontWeight: 600 }}>
                    Ajoutez des desserts optionnels. Chacun peut avoir son propre nombre de personnes et de répétitions.
                </p>
            </div>

            {/* Search */}
            <div className="form-group">
                <label className="form-label">Rechercher un dessert</label>
                <div className="input-icon-wrap">
                    <Search size={16} className="input-icon" />
                    <input
                        className="input"
                        value={query}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                        placeholder="Crêpes, tarte aux pommes…"
                    />
                </div>

                {results.length > 0 ? (
                    <div style={{ marginTop: 8 }}>
                        {results.map((r: Recipe) => (
                            <button key={r.id} className="recipe-search-result" onClick={() => addDessert(r)}>
                                <Cake size={16} color="var(--accent)" />
                                <span style={{ flex: 1, textAlign: 'left' }}>{r.name}</span>
                                <Plus size={14} />
                            </button>
                        ))}
                    </div>
                ) : query.trim() && (
                    <p style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--text-3)', textAlign: 'center' }}>
                        Aucun dessert trouvé dans la base de données.
                    </p>
                )}
            </div>

            {/* Added desserts */}
            {state.desserts.length > 0 && (
                <div>
                    <p className="form-label">Desserts ajoutés</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                        {state.desserts.map((d: Dessert, idx: number) => (
                            <div key={d.recipe_id} className="card" style={{ padding: '14px 16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                    <Cake size={16} color="var(--accent)" />
                                    <span style={{ fontWeight: 600, flex: 1 }}>{(d as Dessert & { _name?: string })._name || d.recipe_id}</span>
                                    <button className="btn btn-icon btn-danger btn-sm" onClick={() => removeDessert(idx)}>
                                        <X size={14} />
                                    </button>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                    <div className="form-group">
                                        <label className="form-label">Personnes</label>
                                        <div className="stepper-full">
                                            <button className="stepper-btn" onClick={() => updateDessert(idx, { nb_persons: Math.max(1, d.nb_persons - 1) })}>−</button>
                                            <span className="stepper-val">{d.nb_persons}</span>
                                            <button className="stepper-btn" onClick={() => updateDessert(idx, { nb_persons: d.nb_persons + 1 })}>+</button>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Fois</label>
                                        <div className="stepper-full">
                                            <button className="stepper-btn" onClick={() => updateDessert(idx, { nb_repetitions: Math.max(1, d.nb_repetitions - 1) })}>−</button>
                                            <span className="stepper-val">{d.nb_repetitions}</span>
                                            <button className="stepper-btn" onClick={() => updateDessert(idx, { nb_repetitions: d.nb_repetitions + 1 })}>+</button>
                                        </div>
                                    </div>
                                </div>
                                <p style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginTop: 8 }}>
                                    → {d.nb_repetitions * d.nb_persons} portion{d.nb_repetitions * d.nb_persons > 1 ? 's' : ''}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                {state.desserts.length === 0 && (
                    <button className="btn btn-ghost btn-full" onClick={onNext}>
                        Passer (sans dessert)
                    </button>
                )}
                <button className="btn btn-primary btn-full" onClick={onNext}>
                    {state.desserts.length > 0 ? 'Suivant — Réviser la liste' : 'Générer la liste →'}
                </button>
            </div>
        </div>
    )
}
